import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAgreementsStore } from "../store/agreements-store";
import { useAgreement } from "@/api/hooks/use-agreements";
import { FileText } from "lucide-react";

export const AgreementViewModal: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isViewModalOpen, viewAgreementName, closeViewModal } =
    useAgreementsStore();

  const currentLanguage = i18n.resolvedLanguage || "en";

  const { data: agreementData, isLoading } = useAgreement(
    viewAgreementName ?? "",
    { enabled: isViewModalOpen && !!viewAgreementName }
  );

  const agreementTitle =
    agreementData?.data?.title?.[currentLanguage] ||
    agreementData?.data?.title?.["en"] ||
    viewAgreementName ||
    "";

  const agreementContent =
    agreementData?.data?.content?.[currentLanguage] ||
    agreementData?.data?.content?.["en"] ||
    "";

  return (
    <Dialog open={isViewModalOpen} onOpenChange={closeViewModal}>
      <DialogContent
        open={isViewModalOpen}
        className="max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 p-0"
      >
        <div className="relative z-[100] focus:outline-none w-full h-full flex flex-col overflow-hidden p-6">
          {/* Header */}
          <DialogHeader>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {agreementTitle || t("agreements.viewTitle")}
              </DialogTitle>
            </div>
            <DialogDescription className="sr-only">
              {t("agreements.viewDescription")}
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto py-4">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("agreements.loading")}
                  </p>
                </div>
              </div>
            )}

            {!isLoading && agreementContent && (
              <div className="prose dark:prose-invert max-w-none">
                <div
                  className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: agreementContent }}
                />
              </div>
            )}

            {!isLoading && !agreementContent && (
              <div className="flex items-center justify-center py-12">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("agreements.noContent")}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter>
            <Button
              variant="darkPrimary"
              onClick={closeViewModal}
              className="w-full sm:w-auto"
            >
              {t("common.close")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
